/**
 * Utility for managing pinned workspace items using API user preferences
 * This is a client-side helper that works with the API hooks
 */

import { UserPreferenceDto } from '@utils/types/user-preferences/user-preferences.ts';

export interface PinnedItem {
  id: string;
  type: 'folder' | 'document';
  name?: string;
  pinnedAt: number;
  sessionId?: string; // Required for documents to fetch them
}

const PINNED_ITEMS_KEY_PREFIX = 'pinned_workspace_';

/**
 * Get the preference key for a specific workspace
 */
export const getPinnedItemsKey = (workspaceId: string = 'root'): string => {
  return `${PINNED_ITEMS_KEY_PREFIX}${workspaceId}`;
};

/**
 * Parse pinned items from a user preference value string
 */
export const parsePinnedItems = (value: string | undefined): PinnedItem[] => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    void error;
    return [];
  }
};

/**
 * Serialize pinned items to a JSON string for storage
 */
export const serializePinnedItems = (items: PinnedItem[]): string => {
  return JSON.stringify(items);
};

/**
 * Get pinned items from user preferences
 */
export const getPinnedItemsFromPreferences = (
  preferences: UserPreferenceDto[] | undefined,
  workspaceId: string = 'root'
): PinnedItem[] => {
  if (!preferences) return [];

  const key = getPinnedItemsKey(workspaceId);
  const matching = preferences.filter(p => p.key === key);
  if (!matching.length) return [];
  // When rapid pins create duplicate preferences, always read the newest (highest ID)
  const newest = matching.reduce((best, p) =>
    (p.id ?? 0) > (best.id ?? 0) ? p : best
  );
  return parsePinnedItems(newest.value);
};

/**
 * Check if an item is pinned
 */
export const isItemPinned = (
  itemId: string,
  preferences: UserPreferenceDto[] | undefined,
  workspaceId: string = 'root'
): boolean => {
  const pinnedItems = getPinnedItemsFromPreferences(preferences, workspaceId);
  return pinnedItems.some(item => item.id === itemId);
};

/**
 * Add an item to pinned items
 */
export const addPinnedItem = (
  itemId: string,
  itemType: 'folder' | 'document',
  itemName: string | undefined,
  preferences: UserPreferenceDto[] | undefined,
  workspaceId: string = 'root',
  sessionId?: string
): { key: string; value: string } => {
  const current = getPinnedItemsFromPreferences(preferences, workspaceId);

  // Don't add if already pinned
  if (current.some(item => item.id === itemId)) {
    return {
      key: getPinnedItemsKey(workspaceId),
      value: serializePinnedItems(current)
    };
  }

  const newItem: PinnedItem = {
    id: itemId,
    type: itemType,
    name: itemName,
    pinnedAt: Date.now(),
    sessionId // Store sessionId for documents
  };

  const updated = [...current, newItem];

  return {
    key: getPinnedItemsKey(workspaceId),
    value: serializePinnedItems(updated)
  };
};

/**
 * Remove an item from pinned items
 */
export const removePinnedItem = (
  itemId: string,
  preferences: UserPreferenceDto[] | undefined,
  workspaceId: string = 'root'
): { key: string; value: string } => {
  const current = getPinnedItemsFromPreferences(preferences, workspaceId);
  const updated = current.filter(item => item.id !== itemId);

  return {
    key: getPinnedItemsKey(workspaceId),
    value: serializePinnedItems(updated)
  };
};

/**
 * Toggle pin state of an item
 */
export const togglePinnedItem = (
  itemId: string,
  itemType: 'folder' | 'document',
  itemName: string | undefined,
  preferences: UserPreferenceDto[] | undefined,
  workspaceId: string = 'root',
  sessionId?: string
): { key: string; value: string; isPinned: boolean } => {
  const isPinned = isItemPinned(itemId, preferences, workspaceId);

  if (isPinned) {
    const result = removePinnedItem(itemId, preferences, workspaceId);
    return { ...result, isPinned: false };
  } else {
    const result = addPinnedItem(
      itemId,
      itemType,
      itemName,
      preferences,
      workspaceId,
      sessionId
    );
    return { ...result, isPinned: true };
  }
};
